import React from 'react';
import { STARTER_GROUP_NAME, STARTER_LOCATION_NAME } from 'utils/constants/starterSite';

interface StarterWelcomeProps {
	siteName?: string;
	// The real names, in case the user already renamed the group or location; fall back to the starter names.
	groupName?: string;
	locationName?: string;
	onSetUpOwnSites: () => void;
}

const ROW_LABEL = 'py-[14px] px-[18px] border-b border-[#ECEDEF] font-jost text-[15px] text-[#556670]';
const ROW_VALUE = 'm-0 py-[14px] px-[18px] border-b border-[#ECEDEF] font-jost text-[16px] text-[#2C2C2C]';

// Shown in place of "No results yet" on a starter site that has no results (PWA-3011, mockup A).
const StarterWelcome: React.FC<StarterWelcomeProps> = ({
	siteName,
	groupName = STARTER_GROUP_NAME,
	locationName = STARTER_LOCATION_NAME,
	onSetUpOwnSites,
}) => (
	<section
		aria-labelledby="starter-welcome-title"
		className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start px-12 pt-10 pb-11 bg-white"
	>
		<div className="flex flex-col gap-[14px]">
			<h2 id="starter-welcome-title" className="m-0 font-inter text-[18px] font-semibold tracking-normal text-[#2C2C2C]">
				Your starter site is ready to test
			</h2>
			<p className="m-0 font-jost text-[16px] leading-6 text-[#556670]">
				We set up a site with one sample location so you can run your first test now. Press{' '}
				<strong className="font-medium text-[#2C2C2C]">Run tests</strong>, choose{' '}
				<strong className="font-medium text-[#2C2C2C]">{locationName}</strong> and the kit you're using,
				then follow the steps.
			</p>
			<p className="m-0 font-jost text-[16px] leading-6 text-[#556670]">
				Rename anything later. Your results stay attached when you do.
			</p>
			<button
				type="button"
				onClick={onSetUpOwnSites}
				className="self-start p-0 bg-transparent border-0 font-inter text-[14px] font-semibold text-[#007AA7] underline hover:text-[#022943] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0093C8]"
			>
				Set up your own sites instead
			</button>
		</div>

		<div className="border border-[#ECEDEF] rounded overflow-hidden">
			<div className="py-3 px-[18px] bg-[#F4F8F9] border-b border-[#ECEDEF] font-inter text-[14px] font-semibold text-[#2C2C2C]">
				What we set up for you
			</div>
			<dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)]">
				<dt className={ROW_LABEL}>Group</dt>
				<dd className={ROW_VALUE}>{groupName}</dd>
				<dt className={ROW_LABEL}>Site</dt>
				<dd className={ROW_VALUE}>{siteName}</dd>
				<dt className={ROW_LABEL}>Sample location</dt>
				<dd className={ROW_VALUE}>{locationName}</dd>
				<dt className={`${ROW_LABEL} border-b-0`}>Test kits</dt>
				<dd className={`${ROW_VALUE} border-b-0`}>
					Your organization's kits. Pick the one you're using when you run a test.
				</dd>
			</dl>
		</div>
	</section>
);

export default StarterWelcome;
